import { useQuery } from '@apollo/client';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { orderTagsTemplate } from 'apollo/templates/order-tags';
import { OrderTag } from 'models/tags';
import React, { FC } from 'react';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useTranslation } from 'react-i18next';
import Loader from '../loader/loader.component';

type OrderTagDropdownProps = {
    selectedTagIds: string[];
    onTagChange: (orderTagIds: string[]) => void;
};

export const OrderTagDropdown: FC<OrderTagDropdownProps> = ({ selectedTagIds, onTagChange }) => {
    const { t: translate } = useTranslation(['orders']);
    const { showBoundary } = useDenthubErrorBoundary();

    const { data: { orderTags } = { orderTags: [] }, loading } = useQuery<{ orderTags: OrderTag[] }>(
        orderTagsTemplate,
        {
            notifyOnNetworkStatusChange: true,
            onError(error) {
                showBoundary(error);
            },
        },
    );

    if (loading) return <Loader />;

    const noTagOption: OrderTag = {
        _id: 'NONE',
        name: translate('orders:NO_ORDER_TAG'),
        color: '#9e9e9e',
    };
    const tagOptions = [noTagOption, ...(orderTags || [])];

    // Convert ids → OrderTag objects
    const selectedTags = tagOptions.filter((tag) => selectedTagIds?.includes(tag._id as string));

    return (
        <Autocomplete
            multiple
            id="orderTag-multiselect-dropdown"
            options={tagOptions}
            value={selectedTags}
            onChange={(_: any, selected: OrderTag[]) => onTagChange(selected.map((tag) => tag._id as string))}
            getOptionLabel={(option: OrderTag) => option?.name || ''}
            isOptionEqualToValue={(option: OrderTag, value: OrderTag) => option?._id === value?._id}
            renderTags={(value: OrderTag[], getTagProps) =>
                value.map((option: OrderTag, index: number) => (
                    <Chip
                        {...getTagProps({ index })}
                        key={option._id}
                        label={option.name}
                        size="small"
                        sx={{
                            backgroundColor: option.color,
                            color: '#fff',
                        }}
                    />
                ))
            }
            renderInput={(params) => <TextField {...params} label={translate('orders:ORDER_TAG')} fullWidth />}
        />
    );
};
